import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectApplication } from '../../../redux/slices/applicationSlice';
import { StepCard, Button } from '../../../components/stepCard';
import StatusPill from '../../../components/statusPill';
import { formatRupees } from '../../../utils/loanMath';
import PageTransition from '../../../components/pageTransition';
import './index.scss';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

// What the applicant sees for each status. Copy is deliberately plain —
// no false reassurance, no jargon, and honest about timelines.
const STATUS_COPY = {
  SUBMITTED: {
    heading: 'Application received',
    body: 'Your application is in the queue for review. We’ll email you when there’s an update.',
    tone: 'review'
  },
  UNDER_REVIEW: {
    heading: 'An underwriter is reviewing your application',
    body: 'A qualified underwriter is going through your details and documents now.',
    tone: 'review'
  },
  MORE_INFO_REQUIRED: {
    heading: 'We need more information',
    body: 'The underwriter has asked for additional details. Check the notes below and update your application.',
    tone: 'pending'
  },
  APPROVED: {
    heading: 'Your loan has been approved',
    body: 'The approved amount and terms are shown below. Our team will contact you about disbursal.',
    tone: 'approved'
  },
  APPROVED_WITH_CONDITIONS: {
    heading: 'Approved, with conditions',
    body: 'Your loan is approved subject to the conditions listed below.',
    tone: 'approved'
  },
  REJECTED: {
    heading: 'We couldn’t approve this application',
    body: 'The underwriter’s reason is below. You may reapply once your circumstances change.',
    tone: 'declined'
  },
  WITHDRAWN: {
    heading: 'Application withdrawn',
    body: 'This application was withdrawn and is no longer being processed.',
    tone: 'neutral'
  }
};

const TIMELINE = [
  {
    key: 'submitted',
    label: 'Application submitted',
    detail: 'Your details and documents were received.'
  },
  {
    key: 'verification',
    label: 'Document verification',
    detail: 'We check your documents against the details you provided.'
  }
  // {
  //   key: 'review',
  //   label: 'Underwriter review',
  //   detail: 'A qualified underwriter assesses your application and makes the decision.'
  // },
  // {
  //   key: 'decision',
  //   label: 'Decision',
  //   detail: 'You’ll be notified by email once a decision is made.'
  // }
];

const stageIndexFor = (status) => {
  switch (status) {
    case 'SUBMITTED':
      return 1;
    case 'UNDER_REVIEW':
      return 2;
    case 'MORE_INFO_REQUIRED':
      return 2;
    case 'APPROVED':
    case 'APPROVED_WITH_CONDITIONS':
    case 'REJECTED':
      return 4;
    default:
      return 0;
  }
};

const StatusStep = () => {
  const navigate = useNavigate();
  const application = useSelector(selectApplication);

  // A draft has nothing to track yet.
  if (application.status === 'DRAFT') {
    return (
      <StepCard
        title="Nothing to track yet"
        description="Finish and submit your application to follow its progress here.">
        <Button type="button" onClick={() => navigate('/apply/review')}>
          Go to review
        </Button>
      </StepCard>
    );
  }

  const copy = STATUS_COPY[application.status] ?? {
    heading: application.status,
    body: '',
    tone: 'neutral'
  };
  const stage = stageIndexFor(application.status);
  const decided = Boolean(application.decisionAt);

  return (
    <PageTransition>
      <div className="status-page">
        <section className={`status-hero status-hero--${copy.tone}`}>
          <StatusPill status={application.status} />
          <h1 className="status-hero__heading">{copy.heading}</h1>
          <p className="status-hero__body">{copy.body}</p>

          <div className="status-hero__ref">
            <span className="status-hero__ref-label">Reference number</span>
            <span className="status-hero__ref-value numeric">{application.referenceNo}</span>
          </div>
        </section>

        <div className="status-grid">
          <div className="status-tile">
            <span className="status-tile__label">Amount requested</span>
            <span className="status-tile__value numeric">
              {formatRupees(application.loanAmount)}
            </span>
            <span className="status-tile__sub numeric">{application.tenureMonths} months</span>
          </div>

          <div className="status-tile">
            <span className="status-tile__label">Estimated EMI</span>
            <span className="status-tile__value numeric">
              {formatRupees(application.snapshotEmi)}
            </span>
            <span className="status-tile__sub numeric">at {application.snapshotRatePct}% p.a.</span>
          </div>

          <div className="status-tile">
            <span className="status-tile__label">Submitted</span>
            <span className="status-tile__value status-tile__value--sm">
              {formatDateTime(application.submittedAt)}
            </span>
          </div>
        </div>

        {decided && (
          <section className="decision-card">
            <h2 className="decision-card__title">Underwriter’s decision</h2>

            {application.approvedAmount && (
              <div className="decision-card__row">
                <span className="decision-card__label">Approved amount</span>
                <span className="decision-card__value numeric">
                  {formatRupees(application.approvedAmount)}
                </span>
              </div>
            )}

            {application.conditionsText && (
              <div className="decision-card__block">
                <span className="decision-card__label">Conditions</span>
                <p className="decision-card__text">{application.conditionsText}</p>
              </div>
            )}

            {application.decisionRemarks && (
              <div className="decision-card__block">
                <span className="decision-card__label">Remarks</span>
                <p className="decision-card__text">{application.decisionRemarks}</p>
              </div>
            )}

            <span className="decision-card__meta">
              Decided {formatDateTime(application.decisionAt)}
            </span>
          </section>
        )}

        <section className="progress-track">
          <h2 className="progress-track__title">What happens next</h2>

          <ol className="progress-track__list">
            {TIMELINE.map((item, index) => {
              const done = index < stage;
              const active = index === stage;

              return (
                <li
                  key={item.key}
                  className={[
                    'track-item',
                    done && 'track-item--done',
                    active && 'track-item--active'
                  ]
                    .filter(Boolean)
                    .join(' ')}>
                  <span className="track-item__marker" aria-hidden="true" />
                  <div className="track-item__body">
                    <span className="track-item__label">{item.label}</span>
                    <span className="track-item__detail">{item.detail}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        {application.status === 'MORE_INFO_REQUIRED' && (
          <div className="status-actions">
            <Button type="button" onClick={() => navigate('/apply/personal')}>
              Update my application
            </Button>
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default StatusStep;
